/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

/**
 * **Changing one copy of a shared value asks about the others** (`#944`, Todd 2026-09-18).
 *
 * > *"When the user updates or changes one of the collection, on apply it asks 'used in x places,
 * > apply to all?'. Saying no doesn't clear the warning."*
 *
 * One hook for the three request editors, so the question is worded once and the answer does the same
 * thing wherever it is given.
 *
 * ## Two answers, and Escape is the one that changes least
 *
 * **Apply to all** changes every place sending the old value: this surface's rows at once, the other
 * surfaces through {@link RequestOccurrences.applyElsewhere} — which on the detail page opens each
 * other section with the change staged, and in the wizard writes its one draft. **Only this one** is
 * the edit exactly as the operator made it, which is why it is the dialog's safe answer: Escape, the
 * backdrop and a reflexive Enter all land on what was already on screen, and nothing else moves.
 *
 * *Only this one* does not clear a conflict. Where the places carry a declared concept, the request
 * now disagrees with itself, and the finding says so until it does not — it describes the request,
 * not the gesture (`requestConflicts`).
 *
 * ## The answer is one commit
 *
 * The editor hands over a `commit` that applies its own Apply *and* the change in one `onChange`.
 * Two calls would each start from the rows the Apply was pressed on, and the second would overwrite
 * the first.
 */

import React from 'react';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import {
  describePlace,
  occurrenceKey,
  otherPlacesSending,
  type OccurrencePlace,
  type RequestOccurrences,
  type ValueChange,
} from './request-occurrences';
import type { MappingLeaf } from './ortb-body-editor.adapters';

/** The change an editor applies to its own rows, and hands on to the others. */
export type LocalValueChange = Omit<ValueChange, 'except'>;

interface Pending {
  self: OccurrencePlace;
  change: LocalValueChange;
  others: OccurrencePlace[];
  commit: (change: LocalValueChange | null) => void;
}

export interface ApplyToAll {
  /**
   * Call from Apply. Commits at once — `commit(null)` — unless the row's **previous** value was sent
   * somewhere else and the row now sends something different, in which case it asks first.
   */
  apply: (args: {
    before: MappingLeaf | null;
    after: MappingLeaf;
    self: OccurrencePlace;
    commit: (change: LocalValueChange | null) => void;
  }) => void;
  /** The question, while one is being asked. Rendered by the editor, anywhere. */
  dialog: React.ReactNode;
}

export function useApplyToAll(occurrences: RequestOccurrences | null): ApplyToAll {
  const [pending, setPending] = React.useState<Pending | null>(null);

  const apply: ApplyToAll['apply'] = ({ before, after, self, commit }) => {
    const fromKey = before ? occurrenceKey(before) : null;
    const toKey = occurrenceKey(after);
    // Nothing shared before, or nothing comparable after (suppressed, emptied, composed): there is no
    // *all* to apply to, and a question about it would be noise.
    const others = fromKey && toKey && toKey !== fromKey ? otherPlacesSending(occurrences, fromKey, self) : [];
    if (!fromKey || others.length === 0) {
      commit(null);
      return;
    }
    setPending({ self, change: { fromKey, to: after }, others, commit });
  };

  const answer = (all: boolean) => {
    if (!pending) return;
    setPending(null);
    pending.commit(all ? pending.change : null);
  };

  // Places on another surface that this caller cannot change — every one of them, where there is no
  // `applyElsewhere`. Said in the question rather than discovered afterwards (`#1045`).
  const unreachable =
    pending && !occurrences?.applyElsewhere ? pending.others.filter((o) => o.surface !== pending.self.surface) : [];
  const reachesOthers = pending ? pending.others.some((o) => o.surface !== pending.self.surface) : false;

  const dialog = pending ? (
    <ConfirmDialog
      title={`Used in ${pending.others.length + 1} places — apply to all?`}
      body={
        <>
          {`The same value is also sent as ${pending.others.map(describePlace).join(', ')}. `}
          {unreachable.length > 0
            ? `Apply to all changes the ones in this editor; ${unreachable.map(describePlace).join(', ')} cannot be changed from here and keeps its value.`
            : reachesOthers
              ? `Apply to all changes them too. ${occurrences?.applyElsewhere?.outcome ?? ''}`
              : 'Apply to all changes them too.'}
          {' Only this one keeps the others as they are.'}
        </>
      }
      confirmLabel="Apply to all"
      cancelLabel="Only this one"
      tone="neutral"
      onConfirm={() => answer(true)}
      onCancel={() => answer(false)}
    />
  ) : null;

  return { apply, dialog };
}
